import React, { useState } from "react";

import { DropzoneAreaBase } from "react-mui-dropzone";
import { StyledButton } from "../BuildlingBlocks/StyledButton";

import { StyledHorizontalSplit } from "../BuildlingBlocks/StyledHorizontalSplit";

import { CloseRedIcon } from "../Icon/CloseRedIcon";
import { PDFFileIcon } from "../Icon/PDFFileIcon";
import { TextFileIcon } from "../Icon/TextFileIcon";
import { TextIcon } from "../TextIcon";
import { DownloadIcon } from "../Icon/DownloadIcon";
import { DownloadBlueIcon } from "../Icon/DownloadBlueIcon";
import { DragDropIcon } from "../Icon/DragDropIcon";
import { DragDropBlueIcon } from "../Icon/DragDropBlueIcon";

import styled from "styled-components";
import { InlineTextEdit } from "../InlineEditor/InlineTextEdit";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";
import { FileData, FileDataType } from "../../rule-engine-core/Common/FileData";

interface IFilePreview {
    file: FileData;
    showPreview?: boolean;
    showFileSize?: boolean;
    showFileName?: boolean;
    showDownload?: boolean;
    index: number;
    onDelete: (file: FileData, index: number) => void;
    onUpdate?: (file: FileData, index: number) => void;
}

export const FilePreview = (props: IFilePreview) => {
    const { file, showPreview, showFileName, showFileSize, showDownload, index, onDelete, onUpdate } = props;

    let icon: JSX.Element | undefined;

    const [fileName, setFileName] = useState<string>(file.fileName);

    if (file.fileType === FileDataType.IMAGE) {
        icon = (
            <img
                className="XFileUpload-PreviewImage"
                src={URL.createObjectURL(new Blob([file.data]))}
                alt={file.fileName}
            />
        );
    } else if (file.fileType === FileDataType.PDF) {
        icon = <PDFFileIcon />;
    } else if (file.fileType === FileDataType.TEXT) {
        icon = <TextFileIcon />;
    } else {
        icon = <TextIcon name="U" />;
    }

    return (
        <>
            {index > 0 && (
                <StyledHorizontalSplit
                    background={UIStyleConstants.WHITE_COLOR}
                    marginTop={0}
                    marginBottom={0}
                />
            )}
            <FilePreviewWrapper>
                {showPreview && <FilePreviewIcon>{icon}</FilePreviewIcon>}
                {showFileName && (
                    <FilePreviewFileName>
                        <InlineTextEdit
                            text={fileName}
                            onEdit={(newValue) => {
                                setFileName(newValue);
                                if (onUpdate) {
                                    onUpdate(file, index);
                                }
                            }}
                        />
                    </FilePreviewFileName>
                )}

                {showFileSize && <FilePreviewFileSize>{file.data.byteLength / 1000} kb</FilePreviewFileSize>}

                {showDownload && (
                    <IconButton
                        href={URL.createObjectURL(file.getDataAsBlob())}
                        download={`${fileName}`}
                        rel="noopener noreferrer"
                    >
                        <DownloadBlueIcon />
                    </IconButton>
                )}

                <IconButton onClick={() => onDelete(file, index)}>
                    <CloseRedIcon />
                </IconButton>
            </FilePreviewWrapper>
        </>
    );
};

interface IFileUploadLayer {
    files: FileData[];
    addFiles: (files: FileData[]) => void;
    onClose?: () => void;
    onDelete: (file: FileData, index: number) => void;
    filesLimit: number;
    showPreview?: boolean;
    showFileName?: boolean;
    showFileSize?: boolean;
    showDownload?: boolean;
    title?: string;
}

export const FileUploadLayer = ({
    files,
    addFiles,
    onClose,
    filesLimit,
    showPreview,
    showDownload,
    showFileName,
    showFileSize,
    onDelete,
    title,
}: IFileUploadLayer) => {
    const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        if (event.clipboardData.files.length) {
            let file: AppGlueAny = {};
            const f = event.clipboardData.files[0];
            const reader = new FileReader();
            reader.onload = (event) => {
                file = {
                    file: f,
                    data: event.target?.result,
                };
                addFiles([file]);
            };
            reader.readAsDataURL(f);
        }
    };

    return (
        <FileControlLayer
            data-testid="fileupload-layer"
            onPaste={onPaste}
        >
            {title && <div className="FileUpload-layerTitle">{title}</div>}
            <DragDropContainer>
                {files.length === 0 ? (
                    <DropzoneAreaEmptyPlaceholder>
                        <div className="FileUpload-layerPlaceHolderIcon">
                            <DragDropIcon />
                        </div>
                        <div className="FileUpload-layerPlaceHolderText">Drag & drop file here</div>
                        <div className="FileUpload-layerPlaceHolderOr">or</div>
                        <StyledButton
                            variant="contained"
                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                        >
                            Browse Files
                        </StyledButton>
                        <div className="FileUpload-layerPlaceHolderHint">Also you can paste file or file name</div>
                    </DropzoneAreaEmptyPlaceholder>
                ) : (
                    <DropzoneAreaPlaceholder>
                        <div className="FileUpload-layerPlaceHolderText">
                            <div className="FileUpload-layerPlaceHolderIcon">
                                <DragDropBlueIcon />
                            </div>
                            Drag & drop file here
                        </div>

                        <StyledButton
                            variant="contained"
                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                        >
                            Browse Files
                        </StyledButton>
                        <div className="FileUpload-layerPlaceHolderHint">Also you can paste file or file name</div>
                    </DropzoneAreaPlaceholder>
                )}

                <DropzoneAreaBase
                    fileObjects={files.map((f) => f.toFileObject())}
                    dropzoneText={""}
                    onAdd={(f) => addFiles(f.map((f) => FileData.fromFileObject(f)))}
                    dropzoneClass={files.length === 0 ? "XFileUpload-root" : "XFileUpload-preview-root"}
                    showFileNames={false}
                    showPreviewsInDropzone={false}
                    filesLimit={filesLimit}
                    // disable for other files
                    getPreviewIcon={() => <></>}
                    showAlerts={false}
                />

                {files.length > 0 && (
                    <FilePriviewList>
                        {files.map((f, idx) => (
                            <FilePreview
                                file={f}
                                key={idx}
                                showPreview={showPreview}
                                showFileName={showFileName}
                                showFileSize={showFileSize}
                                index={idx}
                                onDelete={onDelete}
                                showDownload={showDownload}
                            />
                        ))}
                    </FilePriviewList>
                )}
            </DragDropContainer>

            {onClose && (
                <>
                    <StyledHorizontalSplit
                        marginTop={18}
                        marginBottom={20}
                        background={"" + UIStyleConstants.GRAY_LIGHTER_COLOR}
                    />
                    <div className="FileUpload-layerBottom">
                        <StyledButton
                            variant="contained"
                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                            startIcon={<DownloadIcon />}
                            onClick={onClose}
                            disabled={files.length === 0}
                        >
                            Done
                        </StyledButton>
                    </div>
                </>
            )}
        </FileControlLayer>
    );
};

const FilePriviewList = styled.div`
    background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    padding: 4px 12px;
`;

const FilePreviewWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    padding: 8px 4px;
    justify-content: space-between;
`;

const FilePreviewFileName = styled.div`
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    flex: 1;

    .InlineEdit.InlineEdit-input {
        .MuiInputBase-input {
            background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR} !important;
            border: none !important;
            border-bottom: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR} !important;

            font-family: Inter !important;
            font-style: normal !important;
            font-weight: normal !important;
            font-size: 12px !important;
            line-height: 140% !important;

            /* main / black */

            color: ${UIStyleConstants.BLACK_COLOR} !important;
        }
    }
`;

const IconButton = styled.a`
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    margin-left: auto;

    border: 1px solid transparent;
    cursor: pointer;

    &:hover {
        border: 1px solid ${UIStyleConstants.RED_COLOR};
    }
`;

const FilePreviewFileSize = styled.div`
    min-width: 100px;

    text-align: right;

    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 0em;
    color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
`;

const FilePreviewIcon = styled.div`
    width: 26px;
    margin-right: 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    overflow: hidden;

    .XFileUpload-PreviewImage {
        height: 22px;
    }
`;

const FileControlLayer = styled.div`
    .FileUpload-layerTitle {
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 140%;
        /* or 22px */

        text-align: center;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        margin-bottom: 16px;
    }

    .FileUpload-layerBottom {
        text-align: center;
    }
`;

const DropzoneAreaEmptyPlaceholder = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    background: ${UIStyleConstants.WHITE_DARK_COLOR};
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .FileUpload-layerPlaceHolderIcon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${UIStyleConstants.BLUE_LIGHTER_COLOR};
        margin-bottom: 16px;
    }

    .FileUpload-layerPlaceHolderText {
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 140%;
        text-align: center;

        color: ${UIStyleConstants.BLUE_COLOR};
        margin-bottom: 4px;
    }

    .FileUpload-layerPlaceHolderOr {
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 140%;
        /* identical to box height, or 17px */

        display: flex;
        align-items: center;

        /* main / black */

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        opacity: 0.4;

        margin-bottom: 8px;
    }

    .FileUpload-layerPlaceHolderHint {
        font-style: normal;
        font-weight: 500;
        font-size: 8px;
        line-height: 140%;
        /* or 11px */

        text-align: center;
        letter-spacing: 0.1px;

        /* main / blue */

        color: ${UIStyleConstants.BLUE_COLOR};
        margin-top: 12px;
    }
`;

const DropzoneAreaPlaceholder = styled.div`
    background: ${UIStyleConstants.WHITE_DARK_COLOR};

    flex: 1;

    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    .FileUpload-layerPlaceHolderIcon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 4px;
    }

    .FileUpload-layerPlaceHolderText {
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 140%;
        text-align: center;

        color: ${UIStyleConstants.BLUE_COLOR};
        margin-bottom: 8px;

        display: flex;
        justify-content: center;
        align-items: center;
    }

    .FileUpload-layerPlaceHolderHint {
        font-style: normal;
        font-weight: 500;
        font-size: 8px;
        line-height: 140%;
        /* or 11px */

        text-align: center;
        letter-spacing: 0.1px;

        /* main / blue */

        color: ${UIStyleConstants.BLUE_COLOR};
        margin-top: 8px;
        margin-bottom: 16px;
    }
`;

export const DragDropContainer = styled.div`
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    position: relative;

    border: 1px dashed ${UIStyleConstants.BLUE_COLOR};
    border-radius: 4px;

    min-width: 360px;
    min-height: 253px;

    padding: 4px;

    display: flex;
    flex-direction: column;

    .XFileUpload-root {
        flex: 1;

        display: flex;
        flex-direction: column;

        border: none;
        styled: 1;
        justify-content: center;
        background: transparent;

        .MuiDropzoneArea-textContainer {
            flex: 1;
            width: 100%;
            display: flex;
            flex-direction: row-reverse;
            align-items: center;
            justify-content: center;

            border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
            box-sizing: border-box;
            border-radius: 4px;

            .MuiDropzoneArea-icon {
                height: 16px;
                width: 16px;
                display: none;
            }

            .MuiDropzoneArea-text {
                display: none;
            }
        }
    }

    .XFileUpload-preview-root {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        border: 1px dashed ${UIStyleConstants.BLUE_COLOR};
        border-radius: 4px;
        min-height: 0;
        opacity: 0;
    }
`;
